import React, { useContext, useState, useRef, useEffect } from "react";
import "./Message.css";
import { ReactComponent as SearchIcon } from "../images/search.svg";
import { ReactComponent as SendIcon } from "../images/send.svg";
import CreateCommunityModal from "./components/CreateCommunityModal";
import { ChatContext } from "../../createContext/Chat/ChatContext";
import BackIcon from "../images/BackIcon.png";
import axios from "axios";
// Import a default community image
import DefaultCommunityImage from "../images/default-community.png";
import ProfilePassionsGrid from "../Profile/Components/ProfilePassionsGrid";
import CommunityDetailsSection from "./components/CommunityDetailsSection";

const Message = () => {
  const {
    error,
    userInfo,
    token,
    activeChat,
    inputMessage,
    setInputMessage,
    chats,
    messages,
    handleMessageClick,
    sendMessage,
    updateChats,
    handleBackClick,
    fetchAllMembers,
    unseenCounts,
  } = useContext(ChatContext);
  const [activeTab, setActiveTab] = useState("communities");
  const [allMembersList, setAllMembersList] = useState([]);
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mergedUsers, setMergedUsers] = useState([]);
  const chatContainerRef = useRef(null);
  const [showRightContent, setShowRightContent] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // State for communities
  const [communities, setCommunities] = useState([]);
  const [recommendedCommunities, setRecommendedCommunities] = useState([]);
  const [joinedCommunities, setJoinedCommunities] = useState([]);
  const [activeCommunity, setActiveCommunity] = useState(null);
  const [isCreateCommunityModalOpen, setIsCreateCommunityModalOpen] = useState(false);
  const [communityMessages, setCommunityMessages] = useState([]);
  const [showCommunityDetails, setShowCommunityDetails] = useState(false);
  
  // New state for community search
  const [communitySearchQuery, setCommunitySearchQuery] = useState("");
  const [communitySearchActive, setCommunitySearchActive] = useState(false);

  // Function to get community avatar or default image
  const getCommunityAvatar = (community) => {
    return DefaultCommunityImage;
  };

  // Fetch group messages with loading state
  const fetchGroupMessages = async (groupId) => {
    if (!groupId) return;
    
    try {
      setMessagesLoading(true);
      setCommunityMessages([]); // Clear old messages while loading
      
      const response = await axios.get(
        `${process.env.REACT_APP_CHAT_URL}/api/messages/group/${groupId}`,
        {
          headers: { token },
        }
      );
      setCommunityMessages(response.data);
    } catch (error) {
      console.error("Error fetching group messages:", error);
    } finally {
      setMessagesLoading(false);
    }
  };

  // Send group message
  const sendGroupMessage = async (messageContent) => {
    if (!activeCommunity || !messageContent.trim()) return;
    
    try {
      // Optimistically add the new message to the state without refetching
      const newMessage = {
        _id: Date.now().toString(),
        sender: {
          _id: userInfo._id,
          userName: userInfo.userName
        },
        content: messageContent,
        timestamp: new Date().toISOString()
      };
      
      setCommunityMessages(prev => [...prev, newMessage]);
      const response = await axios.post(
        `${process.env.REACT_APP_CHAT_URL}/api/messages/group/send`,
        {
          groupId: activeCommunity._id,
          content: messageContent,
        },
        { headers: { token } }
      );
      
    } catch (error) {
      console.error("Error sending group message:", error);
      // Refetch in case of error to ensure consistency
      fetchGroupMessages(activeCommunity._id);
    }
  };

  // Fetch recommended communities
  const fetchRecommendedCommunities = async () => {
    try {
      const [recommendedResponse, allResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_CHAT_URL}/api/communities/recommended`,
          { headers: { token } }
        ),
        axios.get(`${process.env.REACT_APP_CHAT_URL}/api/communities/all`)
      ]);
      
      setCommunities(allResponse.data);
      setRecommendedCommunities(recommendedResponse.data);
    } catch (error) {
      console.error("Error fetching communities:", error);
    }
  };

  // Join a community
  const joinCommunity = async (communityId, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    try {
      await axios.post(
        `${process.env.REACT_APP_CHAT_URL}/api/communities/join/${communityId}`,
        {},
        { headers: { token } }
      );
      
      // Refresh recommended and joined communities
      await Promise.all([
        fetchRecommendedCommunities(),
        fetchJoinedCommunities()
      ]);
    } catch (error) {
      console.error("Error joining community:", error);
    }
  };

  // Check if user has joined a community
  const hasUserJoined = (communityId) => {
    return joinedCommunities.some(community => 
      community.id === communityId || community._id === communityId
    );
  };

  // Fetch community details and messages
  const fetchCommunityDetailsAndMessages = async (communityId) => {
    // Don't allow opening non-joined communities
    if (!hasUserJoined(communityId)) {
      return;
    }
    
    try {
      // Fetch community details
      const detailsResponse = await axios.get(
        `${process.env.REACT_APP_CHAT_URL}/api/communities/${communityId}`,
        { headers: { token } }
      );
      
      setActiveCommunity(detailsResponse.data);
      // Fetch group messages
      fetchGroupMessages(communityId);
      setShowRightContent(true);
    } catch (error) {
      console.error("Error fetching community details:", error);
    }
  };

  // Handle community click
  const handleCommunityClick = (community) => {
    if (hasUserJoined(community.id || community._id)) {
      setShowCommunityDetails(false);
      fetchCommunityDetailsAndMessages(community.id || community._id);
      // Force show right content in mobile view
      setShowRightContent(true);
    } else {
      // Optionally show a message that user needs to join first
      console.log("Please join the community first");
    }
  };

  // Fetch joined communities
  const fetchJoinedCommunities = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_CHAT_URL}/api/communities/joined`,
        { headers: { token } }
      );
      setJoinedCommunities(response.data);
    } catch (error) {
      console.error("Error fetching joined communities:", error);
    }
  };

  // Handle community creation
  const handleCommunityCreated = async (newCommunity) => {
    // Update recommended and joined communities lists
    await Promise.all([
      fetchRecommendedCommunities(),
      fetchJoinedCommunities()
    ]);

    // Optionally, set the newly created community as active
    setActiveCommunity(newCommunity);
    fetchGroupMessages(newCommunity._id);
    setShowRightContent(true);
  };

  // Filter communities based on search query
  const filterCommunities = (communityList, query, handleJoined) => {
    if (!query || !communityList) return communityList || [];
    
    return communityList.filter(community => {
      if (handleJoined) {
        if (hasUserJoined(community._id)) {
          return false;
        }
      }
      
      // Match community name
      const nameMatch = community.name && 
                        typeof community.name === 'string' && 
                        community.name.toLowerCase().includes(query.toLowerCase());
      
      // Match community description if available
      const descriptionMatch = community.description && 
                              typeof community.description === 'string' && 
                              community.description.toLowerCase().includes(query.toLowerCase());
      
      // Match community industry if available
      const industryMatch = community.industry && 
                            typeof community.industry === 'string' && 
                            community.industry.toLowerCase().includes(query.toLowerCase());
      
      // Match community location if available
      const locationMatch = community.location && 
                            typeof community.location === 'string' && 
                            community.location.toLowerCase().includes(query.toLowerCase());
            
      // Return true if any field matches
      return nameMatch || descriptionMatch || industryMatch || locationMatch;
    });
  };

  // Get filtered communities based on search query
  const filteredRecommendedCommunities = communitySearchQuery.length > 0 
    ? filterCommunities(communities, communitySearchQuery, true) 
    : recommendedCommunities;
    
  const filteredJoinedCommunities = filterCommunities(joinedCommunities, communitySearchQuery, false);

  // Fetch recommended communities when component mounts or when tab changes
  useEffect(() => {
    if (activeTab === "communities") {
      fetchRecommendedCommunities();
      fetchJoinedCommunities();
    }
  }, [activeTab]);

  // Fetch all members on component mount
  useEffect(() => {
    const fetchMembers = async () => {
      if (fetchAllMembers) {
        const members = await fetchAllMembers(); // Call fetchAllMembers from context
        setAllMembersList(members);
      }
    };
    fetchMembers();
  }, [fetchAllMembers]);

  // Merge users from chats and allMembersList, avoiding duplicates
  useEffect(() => {
    if (!chats.users) return;
    
    const uniqueUsers = [
      ...new Map(
        [...(chats.users || []), ...allMembersList].map((user) => [user.id, user]) // Use Map to remove duplicates
      ).values(),
    ];
    setMergedUsers(uniqueUsers);
  }, [chats.users, allMembersList]);

  const filteredUsers = mergedUsers.filter(
    (user) =>
      user.name &&
      typeof user.name === "string" &&
      user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Scroll to the bottom of the messages when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, communityMessages]);

  // Handle user click to add to chats and set as active chat
  const handleUserSelect = (user) => {
    updateChats(user);
    // Set the clicked user as the active chat
    setSearchActive(false); // Close the search after selection
    setSearchQuery(""); // Clear the search query
  };
  
  const getUnseenCount = (chatId) => {
    const countObj = unseenCounts.find((item) => item.chatId === chatId);
    return countObj ? countObj.unseenCount : 0;
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);

  // Monitor window resize for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 991);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Control navigation menu visibility
  useEffect(() => {
    const navMenu = document.querySelector(".navigationMenu");
    if (navMenu) {
      if (isMobile && (activeChat || (activeTab === "communities" && activeCommunity))) {
        navMenu.style.display = "none";
        // Always make sure right content is visible when community is active
        if (activeCommunity) {
          setShowRightContent(true);
        }
      } else {
        navMenu.style.display = "block";
        if (!activeChat && !(activeTab === "communities" && activeCommunity)) {
          setShowRightContent(false);
        }
      }
    }
  }, [isMobile, activeChat, activeCommunity, activeTab]);

  const renderTabContent = () => {
    if (activeTab === "messages") {
      return (
        <>
          <div className="message-search-container">
            {searchActive ? (
              <img
                src={BackIcon}
                alt="Back"
                className="message-search-icon"
                onClick={() => {
                  setSearchActive(false);
                  setSearchQuery("");
                }}
              />
            ) : (
              <SearchIcon
                className="message-search-icon"
                onClick={() => setSearchActive(true)}
              />
            )}
            <input
              type="text"
              placeholder="Search Direct Messages"
              className="message-search-input"
              value={searchQuery}
              onClick={() => setSearchActive(true)}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus={searchActive}
            />
          </div>

          <div className="message-list">
            {searchActive ? (
              filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className="message-item"
                    onClick={() => handleUserSelect(user)}
                  >
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="message-avatar"
                    />
                    <div className="message-item-content">
                      <div className="message-item-name">{user.name}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-users-found">No users found</div>
              )
            ) : chats.users && chats.users.length > 0 ? (
              chats.users.map((user) => (
                <div
                  key={user.id}
                  className="message-item"
                  onClick={() => handleMessageClick(user)}
                >
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="message-avatar"
                  />
                  <div className="message-item-content">
                    <div className="message-item-name">{user.name}</div>
                  </div>
                  {getUnseenCount(user.id) > 0 && (
                    <span className="unseen-count-badge">
                      {getUnseenCount(user.id)}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="no-chats">Select a chat</div>
            )}
          </div>
        </>
      );
    }

    if (activeTab === "communities") {
      return (
        <>
          <div className="message-search-container">
            {communitySearchActive ? (
              <img
                src={BackIcon}
                alt="Back"
                className="message-search-icon"
                onClick={() => {
                  setCommunitySearchActive(false);
                  setCommunitySearchQuery("");
                }}
              />
            ) : (
              <SearchIcon
                className="message-search-icon"
                onClick={() => setCommunitySearchActive(true)}
              />
            )}
            <input
              type="text"
              placeholder="Search Communities"
              className="message-search-input"
              value={communitySearchQuery}
              onChange={(e) => setCommunitySearchQuery(e.target.value)}
              onClick={() => setCommunitySearchActive(true)}
              autoFocus={communitySearchActive}
            />
          </div>

          <div className="communities-section">
            {(communitySearchQuery.length === 0 || filteredRecommendedCommunities.length > 0) && (
              <>
                <div className="communities-section-header">
                  {filteredRecommendedCommunities.length > 0 && <h3>Recommended Communities</h3>}
                </div>
                <div className="message-list">
                  {filteredRecommendedCommunities?.map((community) => (
                    <div
                      key={community.id || community._id}
                      className="message-item"
                    >
                      <img
                        src={getCommunityAvatar(community)}
                        alt={community.name}
                        className="message-avatar"
                      />
                      <div className="message-item-content">
                        <div className="message-item-name">{community.name}</div>
                        <div className="message-item-handle">
                          {community.memberCount || (community.members && community.members.length) || 0} members
                        </div>
                        {community.industry && (
                          <div className="message-item-industry text-sm">{community.industry}</div>
                        )}
                      </div>
                      <button
                        className="join-button"
                        onClick={(e) => joinCommunity(community.id || community._id, e)}
                      >
                        Join
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {(communitySearchQuery.length === 0 || filteredJoinedCommunities.length > 0) && (
              <>
                <div className="communities-section-header">
                  <h3>Your Communities</h3>
                </div>
                <div className="message-list">
                  {filteredJoinedCommunities?.map((community) => (
                    <div
                      key={community.id || community._id}
                      className="message-item"
                      onClick={() => handleCommunityClick(community)}
                    >
                      <img
                        src={getCommunityAvatar(community)}
                        alt={community.name}
                        className="message-avatar"
                      />
                      <div className="message-item-content">
                        <div className="message-item-name">{community.name}</div>
                        <div className="message-item-handle">
                          {community.memberCount || (community.members && community.members.length) || 0} members
                        </div>
                        {community.industry && (
                          <div className="message-item-industry text-sm">{community.industry}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {communitySearchQuery.length > 0 && 
             filteredRecommendedCommunities.length === 0 && 
             filteredJoinedCommunities.length === 0 && (
              <div className="no-communities-found">
                No communities found matching "{communitySearchQuery}"
              </div>
            )}

            <div className="create-community-button">
              <button onClick={() => setIsCreateCommunityModalOpen(true)}>
                + Create Community
              </button>
            </div>
          </div>
        </>
      );
    }
  };

  const renderRightContent = () => {
    // If an active community is selected in communities tab
    const contentClass = `message-right-content ${showRightContent ? 'active' : ''}`;
    if (activeTab === "communities" && activeCommunity) {
      return (
        <div className={contentClass}>
          <div className="message-right-header" style={{cursor:"pointer"}}>
            <div
              className="mobile-back-button"
              onClick={() => {
                setShowCommunityDetails(false);
                setActiveCommunity(null);
                // Add this line to ensure proper mobile navigation
                if (isMobile) {
                  setShowRightContent(false);
                }
              }}
            >
              ←
            </div>
            <img
              src={getCommunityAvatar(activeCommunity)}
              alt={activeCommunity.name}
              className="message-user-avatar"
            />
            <h3 onClick={() => setShowCommunityDetails(!showCommunityDetails)}>
              {activeCommunity.name}
            </h3>
          </div>

          {!showCommunityDetails && (
            <>
              <div className="message-chat-container" ref={chatContainerRef}>
                {messagesLoading ? (
                  <div className="loading-messages">Loading messages...</div>
                ) : communityMessages.length > 0 ? (
                  communityMessages?.map((message) => (
                    <div
                      key={message._id}
                      className={`message-chat-message ${
                        message.sender._id === userInfo._id
                          ? "message-chat-self"
                          : "message-chat-other"
                      }`}
                    >
                      {/* Add the sender name above the message for community chats */}
                      <div
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: "bold",
                          marginBottom: "2px",
                          color: "black",
                          textAlign:
                            message.sender._id !== userInfo._id
                              ? "left"
                              : "right",
                        }}
                      >
                        <a href={message.sender._id !== userInfo._id ? `/profile/${message.sender.userName}` : '#'}>
                          {message.sender.userName || "Unknown User"}
                        </a>
                      </div>

                      <p className="message-chat-text">{message.content}</p>
                      <span className="message-chat-time">
                        {new Date(message.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="no-messages">
                    No messages in this community yet.
                  </div>
                )}
              </div>

              <div className="message-input-container">
                <input
                  type="text"
                  placeholder="Send a message to the community"
                  className="message-input"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && inputMessage.trim()) {
                      sendGroupMessage(inputMessage);
                      setInputMessage(""); // Clear input after sending
                    }
                  }}
                />
                <button
                  className="message-send-button"
                  onClick={() => {
                    if (inputMessage.trim()) {
                      sendGroupMessage(inputMessage);
                      setInputMessage(""); // Clear input after sending
                    }
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <SendIcon />
                </button>
              </div>
            </>
          )}

          {/* Community Details Section */}
          {showCommunityDetails && (
            <CommunityDetailsSection
              activeCommunity={activeCommunity}
              setShowCommunityDetails={setShowCommunityDetails}
              showCommunityDetails={showCommunityDetails}
            />
          )}
        </div>
      );
    }

    // Existing right content for messages...
    return (
      <div className={contentClass}>
        <div className="message-right-header">
          <div className="mobile-back-button" onClick={handleBackClick}>
            ←
          </div>
          {activeChat && (
            <img
              src={activeChat.avatar}
              alt={activeChat.name}
              className="message-user-avatar"
            />
          )}
          <h3>{activeChat ? <a href={`/profile/${activeChat.userName}`}>{activeChat.name}</a> : "Select a chat"}</h3>
        </div>
        <div className="message-chat-container" ref={chatContainerRef}>
          {activeChat && messages.length > 0 ? (
            messages.map((message) => (
              <div
                key={message._id}
                className={`message-chat-message ${
                  message.sender._id === userInfo._id ||
                  message.sender === userInfo._id
                    ? "message-chat-self"
                    : "message-chat-other"
                }`}
              >
                <p className="message-chat-text">{message.content}</p>
                <span className="message-chat-time">
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </span>
              </div>
            ))
          ) : (
            <div className="no-messages">No messages yet.</div>
          )}
        </div>
        {activeChat ? (
          <div className="message-input-container">
            <input
              type="text"
              placeholder="Start a new message"
              className="message-input"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && inputMessage.trim()) {
                  sendMessage(inputMessage);
                  setInputMessage(""); // Clear input after sending
                }
              }}
            />
            <button
              className="message-send-button"
              onClick={() => {
                if (inputMessage.trim()) {
                  sendMessage(inputMessage);
                  setInputMessage(""); // Clear input after sending
                }
              }}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              <SendIcon />
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="message-container">
      {error && <div className="error-banner">{error}</div>}

      <div className="message-middle-content scrollbar-hide">
        <div className="message-tabs">
          <button
            className={`tab ${activeTab === "communities" ? "active" : ""}`}
            onClick={() => setActiveTab("communities")}
          >
            Communities
          </button>
          <button
            className={`tab ${activeTab === "messages" ? "active" : ""}`}
            onClick={() => setActiveTab("messages")}
          >
            Messages
          </button>
        </div>

        {renderTabContent()}
      </div>

      {renderRightContent()}
      <CreateCommunityModal
        isOpen={isCreateCommunityModalOpen}
        onClose={() => setIsCreateCommunityModalOpen(false)}
        onCommunityCreated={handleCommunityCreated}
      />
    </div>
  );
};

export default Message;